import { AfterViewInit, Component, OnDestroy, OnInit, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { Subject, filter, take, takeUntil } from 'rxjs';
import { HcModal, HcSort, HcTableDataSource, ModalOptions, ModalService, PaginationComponent, SimpleModalOptions } from '@healthcatalyst/cashmere';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ProductLineElement, ProductLineService } from 'src/app/shared/services/product-line.service';

/** Admin list of product lines to edit */
@Component({
    selector: 'pd-admin-product-lines',
    templateUrl: './admin-product-lines.component.html',
    styleUrls: ['./admin-product-lines.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class AdminProductLinesComponent implements OnInit, AfterViewInit, OnDestroy {
    private unsubscribe = new Subject<void>();
    dataSource: HcTableDataSource<ProductLineElement> = new HcTableDataSource();
    loading = true;
    loadError = false;
    adminProductLinesDisplayedColumns: string[] = ['id', 'name', 'icon', 'actions'];
    adminProductLinesPageSize: number = 10;
    private modalRef: HcModal<TemplateRef<any>>;
    adminProductLineControl = new FormGroup({
        id: new FormControl(0, { nonNullable: true }),
        name: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
        icon: new FormControl<number | null>(null, { validators: [Validators.required] })
    });

    icons = ['analytics.svg','armus.svg','finance.svg','interoperability.svg','platform.svg','population-health.svg','quality.svg','tems.svg'];

    @ViewChild(HcSort)
    sort: HcSort;

    @ViewChild(PaginationComponent)
    paginator: PaginationComponent;

    get adminProductLinesListLength(): number {
        return this.dataSource.filteredData ? this.dataSource.filteredData.length : 0;
    }

    constructor(private productLineService: ProductLineService, private modalService: ModalService) { }

    ngOnInit(): void {
        this.productLineService.productLineListUpdated.pipe(takeUntil(this.unsubscribe)).subscribe(list => {
            if (list) {
                this.loading = false;
                this.loadError = false;
                this.dataSource = new HcTableDataSource(list);
                if (this.sort && this.paginator) {
                    this.dataSource.sort = this.sort;
                    this.dataSource.paginator = this.paginator;
                }
            } else {
                this.loadError = this.productLineService.loadFailed;
                if (!this.loadError) {
                    this.loadProductLines();
                } else {
                    this.loading = false;
                }
            }
        });
    }

    ngAfterViewInit(): void {
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;
    }

    loadProductLines(): void {
        this.productLineService.refresh();
    }

    applyFilter(filter: string): void {
        this.dataSource.filter = filter.trim().toLowerCase();
    }

    openAddEditModal(content: TemplateRef<any>, edit: boolean, productLine?: ProductLineElement) {
        const options: ModalOptions = {
            size: 'md',
            data: {
                productLine: productLine,
                edit: edit
            }
        };
        this.adminProductLineControl.reset();

        if (productLine) {
            this.adminProductLineControl.patchValue({
                id: productLine.productLineID,
                name: productLine.name,
                icon: productLine.icon
            });
        }
        this.modalRef = this.modalService.open(content, options);
    }


    editProductLine() {
        const target: ProductLineElement = {
            productLineID: this.adminProductLineControl.controls['id'].value,
            name: this.adminProductLineControl.controls['name'].value,
            icon: this.adminProductLineControl.controls['icon'].value !== null ? this.adminProductLineControl.controls['icon'].value : -1
        };
        this.productLineService.update(target);
        this.modalRef.close();
    }

    addProductLine() {
        const target: ProductLineElement = {
            productLineID: -1,
            name: this.adminProductLineControl.controls['name'].value,
            icon: this.adminProductLineControl.controls['icon'].value !== null ? this.adminProductLineControl.controls['icon'].value : -1
        };
        this.productLineService.add(target);
        this.modalRef.close();
    }

    removeProductLine(element: ProductLineElement) {
        const options: SimpleModalOptions = {
            messageHTML: '<p>Are you sure you want to remove the product line: <strong>' + element.name + '</strong>?<p>',
            headerText: 'Delete Product Line',
            confirmButtonText: 'Delete Product Line',
            icon: ''
        };
        this.modalService.confirmDestructive(options)
            .pipe(take(1), filter(confirmed => confirmed))
            .subscribe(() => {
                this.productLineService.remove(element);
            });
    }

    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}